import * as Api from '@ViewModels';
import * as React from 'react';
import { convertRawRichTextContentStateToRichContentEditorState } from '../../../../models/UiUtils';
import { useBaseStepState } from './useBaseStepState';

interface UseActionItemStepStateParams {
	actionItemStep: Api.IActionItemAutomationStep | null;
	template: Api.IAutomationTemplate | null | undefined;
	stepId: string;
	readonly: boolean;
	onSaveSuccess?: () => void;
	onSaveError?: (error: Api.IOperationResultNoValue) => void;
}

/**
 * Custom hook for managing action item step state and save logic.
 * Extends the base step state with action item-specific functionality.
 */
export function useActionItemStepState({
	actionItemStep,
	template,
	stepId,
	readonly,
	onSaveSuccess,
	onSaveError,
}: UseActionItemStepStateParams) {
	// Action item-specific state
	const [actionItemContentEditorState, setActionItemContentEditorState] =
		React.useState<Api.IRichContentEditorState | null>(() => {
			if (actionItemStep?.content) {
				return convertRawRichTextContentStateToRichContentEditorState(actionItemStep.content);
			}
			return null;
		});

	// Use base hook for common functionality
	const baseState = useBaseStepState({
		step: actionItemStep,
		template,
		stepId,
		readonly,
		onSaveSuccess,
		onSaveError,
		initialContactFilter: actionItemStep?.contactFilter,
		hasAdvancedOptions: step => {
			return !!step.contactFilter?.criteria || !!step.sendFromOptions;
		},
	});

	// Action item-specific save function
	// Destructure specific functions from baseState to avoid unstable dependencies
	const { baseSave, skipStepContactFilter } = baseState;
	const save = React.useCallback(
		({
			newContactFilter = skipStepContactFilter,
			newSendFrom = null,
		}: {
			newContactFilter?: Api.IContactFilterCriteria;
			newSendFrom?: Api.ISendFromOptions | null;
		} = {}) => {
			const buildStepModel = (step: Api.IActionItemAutomationStep): Api.IActionItemAutomationStep => {
				const stepModel: Api.IActionItemAutomationStep = {
					...step,
					content: actionItemContentEditorState?.getRawRichTextContent(),
					contactFilter: newContactFilter,
				};

				if (newSendFrom != null) {
					stepModel.sendFromOptions = newSendFrom;
				}

				return stepModel;
			};

			return baseSave(Api.AutomationStepType.ActionItem, buildStepModel);
		},
		[actionItemContentEditorState, baseSave, skipStepContactFilter]
	);

	return {
		// Action item-specific state
		actionItemContentEditorState,
		setActionItemContentEditorState,
		// Base state
		skipStepContactFilter: baseState.skipStepContactFilter,
		setSkipStepContactFilter: baseState.setSkipStepContactFilter,
		showRequiredFieldError: baseState.showRequiredFieldError,
		advancedOptionsToggled: baseState.advancedOptionsToggled,
		setAdvancedOptionsToggled: baseState.setAdvancedOptionsToggled,
		autoSave: baseState.autoSave,
		clearInlineErrorMessage: baseState.clearInlineErrorMessage,
		// Action item save
		save,
	};
}
